import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Routes } from '@angular/router';

import { CatalogFacade } from './core/catalog.facade';
import { customerAuthGuard } from './core/customer-auth.guard';
import { PRODUCT_LIST_PAGE_SIZE } from './features/products/product-list.constants';

const homeResolver = () => inject(CatalogFacade).home();

const productsResolver = (route: ActivatedRouteSnapshot) =>
  inject(CatalogFacade).products({
    query: route.queryParamMap.get('q') ?? undefined,
    categoryId: Number(route.queryParamMap.get('categoryId')) || undefined,
    brandId: Number(route.queryParamMap.get('brandId')) || undefined,
    sort: route.queryParamMap.get('sort') ?? 'newest',
    // URL `page` is 1-based (what the user sees); the backend is 0-based. No param ⇒ page 1.
    page: Math.max(0, (Number(route.queryParamMap.get('page')) || 1) - 1),
    size: PRODUCT_LIST_PAGE_SIZE,
  });

const productResolver = (route: ActivatedRouteSnapshot) => {
  const slugAndId = route.paramMap.get('slugAndId') ?? '';
  const id = Number(slugAndId.match(/-(\d+)$/)?.[1] ?? slugAndId);
  return inject(CatalogFacade).product(id);
};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent),
    resolve: { home: homeResolver },
  },
  {
    path: 'products',
    loadComponent: () =>
      import('./features/products/products.component').then((m) => m.ProductsComponent),
    resolve: { products: productsResolver },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
  },
  {
    path: 'search',
    loadComponent: () =>
      import('./features/products/products.component').then((m) => m.ProductsComponent),
    resolve: { products: productsResolver },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
  },
  {
    path: 'c/:categorySlug',
    loadComponent: () =>
      import('./features/products/products.component').then((m) => m.ProductsComponent),
    resolve: { products: productsResolver },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
  },
  {
    path: 'product/:slugAndId',
    loadComponent: () =>
      import('./features/product/product.component').then((m) => m.ProductComponent),
    resolve: { product: productResolver },
  },
  {
    path: 'compare',
    loadComponent: () =>
      import('./features/compare/compare.component').then((m) => m.CompareComponent),
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./features/auth/auth.component').then((m) => m.AuthComponent),
  },
  {
    path: 'account',
    canActivate: [customerAuthGuard],
    loadComponent: () =>
      import('./features/account/profile.component').then((m) => m.ProfileComponent),
  },
  {
    path: 'account/orders',
    canActivate: [customerAuthGuard],
    loadComponent: () =>
      import('./features/account/orders.component').then((m) => m.OrdersComponent),
  },
  {
    path: 'account/orders/:id',
    canActivate: [customerAuthGuard],
    loadComponent: () =>
      import('./features/account/order-detail.component').then(
        (m) => m.OrderDetailComponent,
      ),
  },
  {
    path: 'cart',
    loadComponent: () => import('./features/cart/cart.component').then((m) => m.CartComponent),
  },
  {
    path: 'checkout',
    loadComponent: () =>
      import('./features/checkout/checkout.component').then((m) => m.CheckoutComponent),
  },
  {
    path: 'order/success/:id',
    loadComponent: () =>
      import('./features/orders/order-success.component').then((m) => m.OrderSuccessComponent),
  },
  {
    path: 'payment/momo-return',
    loadComponent: () =>
      import('./features/payment/momo-return.component').then((m) => m.MomoReturnComponent),
  },
  {
    path: 'track',
    loadComponent: () =>
      import('./features/orders/track.component').then((m) => m.TrackComponent),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];
